import {
  FormatByCountryType,
  LOCALE_BY_COUNTRY,
  resolveCountry,
  separatorsByCountry,
} from "./FormatByCountry";

/** Alias — selaras FormatByCountryType; jangan definisi ulang union. */
export type FormatInputByCountryType = FormatByCountryType;

export type FormatInputByCountryOptions = {
  type: FormatInputByCountryType;
  /**
   * Optional API bawaan override (caller passes entity country when present).
   * Omit → Account.countryCode via resolveCountry, fallback "ID".
   * Util NEVER reads form origin/dest district or $i18n.locale.
   */
  country?: string;
  /** Max digit desimal (Intl.maximumFractionDigits). Cap saat ketik — tidak pad zero seperti display.
   * Omit → default by type: money=2; quantity=0; weight/dimension/percentage = tanpa cap.
   * Override eksplisit tetap (contoh COD 10, komisi 2). */
  maxFractionDigits?: number;
};

export type ParseByCountryOptions = {
  /** Precedence selaras FormatInputByCountryOptions.country — API bawaan or omit → auth. */
  country?: string;
};

/** Default ikut existing Medusa — jangan invent cap (mis. 20). */
function defaultMaxFractionDigits(
  type: FormatInputByCountryType
): number | undefined {
  if (type === "money") return 2;
  if (type === "quantity") return 0;
  return 2; // weight/dimension/percentage: 2 max digit
}

/** Parse string tampilan → angka mentah (BE-ready). */
export function parseByCountry(
  raw: string | number,
  options?: ParseByCountryOptions
): number {
  if (typeof raw === "number") {
    return Number.isFinite(raw) ? raw : 0;
  }
  const str = String(raw ?? "").trim();
  if (!str) return 0;

  const country = resolveCountry(options?.country);
  let normalized = str;
  if (country === "MY") {
    // Hapus `,` ribuan; biarkan `.` desimal
    normalized = str.replace(/,/g, "");
  } else {
    // Hapus `.` ribuan; ganti `,` → `.` (perilaku existing toNumber)
    normalized = str.replace(/\./g, "").replace(",", ".");
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
}

/** Format as-you-type → string tampilan dengan separator country. */
export default function formatInputByCountry(
  raw: string | number,
  options: FormatInputByCountryOptions
): string {
  const country = resolveCountry(options.country);
  const locale = LOCALE_BY_COUNTRY[country];
  const { thousand, decimal } = separatorsByCountry(country);
  const maxFractionDigits =
    options.maxFractionDigits ?? defaultMaxFractionDigits(options.type);
  const allowFraction = maxFractionDigits === undefined || maxFractionDigits > 0;

  const input = String(raw ?? "");
  const trailingDecimal =
    allowFraction &&
    (input.endsWith(decimal) ||
      (country === "MY" && input.endsWith(".")) ||
      (country === "ID" && input.endsWith(",")));

  // Ambil digit + pemisah yang relevan saja
  const cleaned = input.replace(/[^\d.,-]/g, "");
  const decimalIndex = cleaned.lastIndexOf(
    country === "MY" ? "." : ","
  );

  let intRaw = cleaned;
  let fracRaw = "";
  if (decimalIndex >= 0 && allowFraction) {
    intRaw = cleaned.slice(0, decimalIndex);
    fracRaw = cleaned.slice(decimalIndex + 1).replace(/[^\d]/g, "");
    if (maxFractionDigits !== undefined) {
      fracRaw = fracRaw.slice(0, maxFractionDigits);
    }
  }

  intRaw = intRaw.split(thousand).join("").replace(/[^\d-]/g, "");
  const negative = intRaw.startsWith("-");
  const absInt = intRaw.replace(/-/g, "");
  const intNumber = absInt === "" ? 0 : Number(absInt);
  const grouped = new Intl.NumberFormat(locale, {
    useGrouping: true,
    maximumFractionDigits: 0,
  }).format(Number.isFinite(intNumber) ? intNumber : 0);

  const sign = negative ? "-" : "";
  if (maxFractionDigits === 0) {
    return `${sign}${grouped}`;
  }
  if (trailingDecimal && fracRaw === "") {
    return `${sign}${grouped}${decimal}`;
  }
  if (fracRaw !== "") {
    return `${sign}${grouped}${decimal}${fracRaw}`;
  }
  return `${sign}${grouped}`;
}

/** Raw angka (BE / hasil parseByCountry, desimal ".") → string input sesuai country. */
export function formatRawInputByCountry(
  raw: string | number | null | undefined,
  options: FormatInputByCountryOptions
): string {
  if (raw === null || raw === undefined || String(raw).trim() === "") {
    return "";
  }
  const value = Number(raw);
  if (!Number.isFinite(value)) {
    return formatInputByCountry(raw, options);
  }
  const { decimal } = separatorsByCountry(resolveCountry(options.country));
  return formatInputByCountry(String(value).replace(".", decimal), options);
}

/** Format money per currency: hanya ID pakai "," desimal; currency lain (MY, SG, dst.) pakai ".". */
export function resolveMoneyFormatCountry(raw?: string): "ID" | "MY" {
  const code = String(raw ?? "")
    .trim()
    .toUpperCase();
  if (!code) return resolveCountry();
  return code === "ID" || code === "IDN" ? "ID" : "MY";
}
